import React, { useState, useEffect } from 'react';
import { 
  useStripe, 
  useElements, 
  PaymentElement,
  Elements 
} from '@stripe/react-stripe-js';
import { useStripeContext } from '../../contexts/StripeContext';
import { paymentService } from '../../services/payments';
import Button from '../ui/Button';
import AppIcon from '../AppIcon';

// Inner form component that uses Stripe hooks
const StripePaymentFormInner = ({
  clientSecret,
  amount,
  currency = 'INR',
  orderData,
  customerInfo,
  onSuccess,
  onError,
  confirmButtonText = 'Confirm Booking & Pay',
  className = ''
}) => {
  const stripe = useStripe();
  const elements = useElements();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [paymentReady, setPaymentReady] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [successData, setSuccessData] = useState(null);

  // Reset error when clientSecret changes
  useEffect(() => {
    setErrorMessage('');
    setPaymentReady(false);
  }, [clientSecret]);

  // Check if payment element is ready
  useEffect(() => {
    if (elements) {
      const paymentElement = elements?.getElement(PaymentElement);
      if (paymentElement) {
        paymentElement?.on('ready', () => {
          setPaymentReady(true);
        });
        
        paymentElement?.on('change', (event) => {
          if (event?.error) {
            setErrorMessage(event?.error?.message);
          } else {
            setErrorMessage('');
          }
        });
      }
    }
  }, [elements]);

  const handleSubmit = async (event) => {
    event?.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      setErrorMessage('Payment system not ready. Please try again.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage('');

    try {
      // Extract order info for confirmation
      const { orderId, orderNumber } = orderData || {};

      // Confirm payment with Stripe
      const { error: stripeError, paymentIntent } = await stripe?.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location?.origin}/booking-success?order_id=${orderId}`,
        },
        redirect: 'if_required'
      });

      if (stripeError) {
        // Payment failed
        let userFriendlyMessage = 'Payment failed. Please try again.';
        
        switch (stripeError?.code) {
          case 'card_declined':
            userFriendlyMessage = 'Your card was declined. Please try a different payment method.';
            break;
          case 'insufficient_funds':
            userFriendlyMessage = 'Insufficient funds. Please check your account balance.';
            break;
          case 'expired_card':
            userFriendlyMessage = 'Your card has expired. Please use a different card.';
            break;
          case 'incorrect_cvc':
            userFriendlyMessage = 'Incorrect security code. Please check your card details.';
            break;
          case 'processing_error':
            userFriendlyMessage = 'Processing error. Please try again in a moment.';
            break;
          default:
            userFriendlyMessage = stripeError?.message || 'Payment failed. Please try again.';
        }
        
        setErrorMessage(userFriendlyMessage);
        onError?.(stripeError);
      } else if (paymentIntent) {
        // Payment succeeded
        if (paymentIntent?.status === 'succeeded') {
          // Confirm payment on our backend
          try {
            const confirmData = await paymentService?.confirmPayment(paymentIntent?.id);
            
            const successResult = {
              paymentIntent,
              orderId,
              orderNumber,
              orderData: confirmData?.order,
              bookingConfirmed: true
            };

            // Store success data and show animation
            setSuccessData(successResult);
            setShowSuccessAnimation(true);

            // After animation completes, call onSuccess
            setTimeout(() => {
              setShowSuccessAnimation(false);
              onSuccess?.(successResult);
            }, 3500); // Show animation for 3.5 seconds
          } catch (confirmError) {
            console.warn('Payment succeeded but confirmation failed:', confirmError);
            const successResult = {
              paymentIntent,
              orderId,
              orderNumber,
              warning: 'Payment processed successfully, but booking confirmation is pending. Please contact support if needed.',
              bookingConfirmed: false
            };
            
            setSuccessData(successResult);
            setShowSuccessAnimation(true);
            
            setTimeout(() => {
              setShowSuccessAnimation(false);
              onSuccess?.(successResult);
            }, 3500);
          }
        } else if (paymentIntent?.status === 'requires_action') {
          // Additional authentication required
          setErrorMessage('Additional authentication required. Please complete the verification.');
        } else {
          setErrorMessage('Payment processing incomplete. Please try again.');
        }
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      setErrorMessage('An unexpected error occurred. Please try again.');
      onError?.(error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Show loading state while payment system initializes
  if (!stripe || !elements || !clientSecret) {
    return (
      <div className={`bg-white rounded-xl border border-gray-200 shadow-sm p-6 ${className}`}>
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
          <div className="h-12 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="h-12 bg-gray-200 rounded"></div>
        </div>
        <p className="text-sm text-gray-500 mt-4 text-center">
          Loading secure payment system...
        </p>
      </div>
    );
  }

  return (
    <div className={`relative bg-white rounded-xl border border-gray-200 shadow-sm p-6 ${className}`}>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Payment Header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Payment Details</h3>
            <p className="text-sm text-gray-600">
              Secure payment powered by Stripe
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-blue-600">
              {paymentService?.formatAmount(amount, currency)}
            </p>
            <p className="text-sm text-gray-500">Total Amount</p>
          </div>
        </div>

        {/* Payment Element */}
        <div className="space-y-4">
          <PaymentElement 
            options={{
              fields: {
                billingDetails: 'auto'
              },
              layout: {
                type: 'tabs',
                defaultCollapsed: false
              }
            }}
          />
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
            <AppIcon name="AlertCircle" size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-800 text-sm font-medium">Payment Error</p>
              <p className="text-red-700 text-sm mt-1">{errorMessage}</p>
            </div>
          </div>
        )}

        {/* Booking Summary for Travel */}
        {orderData?.items && (
          <div className="bg-blue-50 rounded-lg p-4 space-y-2">
            <h4 className="font-medium text-blue-900">Booking Summary</h4>
            {orderData?.items?.map((item, index) => (
              <div key={index} className="flex justify-between text-sm">
                <span className="text-blue-800">
                  {item?.name} × {item?.traveler_count} traveler{item?.traveler_count > 1 ? 's' : ''}
                </span>
                <span className="text-blue-900 font-medium">
                  {paymentService?.formatAmount(item?.total_price, currency)}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={!paymentReady || isProcessing || !stripe || !elements}
          loading={isProcessing}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white"
          size="lg"
          iconName={isProcessing ? "Loader2" : "CreditCard"}
        >
          {isProcessing ? 'Processing Payment...' : confirmButtonText}
        </Button>

        {/* Security Notice */}
        <div className="flex items-center justify-center space-x-2 text-xs text-gray-500">
          <AppIcon name="Shield" size={14} />
          <span>Your payment information is encrypted and secure</span>
        </div>

        {/* Travel-specific Terms */}
        <div className="text-xs text-gray-500 space-y-1">
          <p>By completing this payment, you agree to HoliYay's booking terms and conditions.</p>
          <p>Cancellation and refund policies apply as per the package terms.</p>
        </div>
      </form>
      {/* Success Animation Overlay */}
      {showSuccessAnimation && (
        <div className="absolute inset-0 bg-white/98 backdrop-blur-sm rounded-xl flex items-center justify-center z-50">
          <div className="text-center space-y-6 p-8 max-w-sm">
            {/* Animated Success Icon */}
            <div className="relative mx-auto w-20 h-20">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center animate-pulse">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center animate-bounce">
                  <AppIcon name="Check" size={28} className="text-white" />
                </div>
              </div>
              {/* Ripple Effects */}
              <div className="absolute inset-0 border-4 border-green-300 rounded-full animate-ping opacity-30"></div>
              <div className="absolute inset-0 border-2 border-green-400 rounded-full animate-ping opacity-50" 
                   style={{ animationDelay: '0.5s' }}></div>
            </div>

            {/* Success Message */}
            <div className="space-y-3">
              <div className="space-y-1">
                <h3 className="text-2xl font-bold text-green-600">
                  🎉 Booking Confirmed!
                </h3>
                <p className="text-green-700 font-medium">
                  Payment successful
                </p>
              </div>
              
              {successData?.orderNumber && (
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-sm text-green-800 font-medium">
                    Booking Reference: {successData?.orderNumber}
                  </p>
                </div>
              )}
              
              {successData?.warning && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-xs text-yellow-800">{successData?.warning}</p>
                </div>
              )}
            </div>

            {/* Amount Confirmation */}
            <div className="bg-green-50 rounded-lg p-4">
              <p className="text-lg font-bold text-green-800">
                {paymentService?.formatAmount(amount, currency)} Paid
              </p>
              <p className="text-xs text-green-600 mt-1">
                Confirmation email will be sent shortly
              </p>
            </div>

            {/* Loading Dots */}
            <div className="flex justify-center space-x-1">
              {[0, 1, 2]?.map((i) => (
                <div 
                  key={i}
                  className="w-2 h-2 bg-green-500 rounded-full animate-bounce"
                  style={{ animationDelay: `${i * 0.1}s` }}
                ></div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Wrapper component that provides Elements context
const StripePaymentForm = (props) => {
  const { stripePromise, stripeOptions } = useStripeContext();

  if (!props?.clientSecret) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="text-center space-y-4">
          <div className="animate-spin w-8 h-8 mx-auto">
            <AppIcon name="Loader2" size={32} className="text-blue-600" />
          </div>
          <p className="text-gray-600">
            Preparing secure payment system...
          </p>
        </div>
      </div>
    );
  }

  // Elements options with client secret and customer info
  const elementsOptions = {
    clientSecret: props?.clientSecret,
    ...stripeOptions,
    defaultValues: props?.customerInfo ? {
      billingDetails: {
        name: `${props?.customerInfo?.firstName || ''} ${props?.customerInfo?.lastName || ''}`?.trim(),
        email: props?.customerInfo?.email || '',
        phone: props?.customerInfo?.phone || '',
        address: {
          line1: props?.customerInfo?.billing?.address_line_1 || '',
          line2: props?.customerInfo?.billing?.address_line_2 || '',
          city: props?.customerInfo?.billing?.city || '',
          state: props?.customerInfo?.billing?.state || '',
          postal_code: props?.customerInfo?.billing?.postal_code || '',
          country: props?.customerInfo?.billing?.country || 'IN',
        }
      }
    } : undefined
  };

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <StripePaymentFormInner {...props} />
    </Elements>
  );
};

export default StripePaymentForm;